/**
 * Bảng máy vò cho MÁY TRẠM SẢN XUẤT — chỉ xem, không điều khiển.
 *
 * Dòng máy ở đây khác dòng trong màn quản lý: chữ to, đọc từ xa, không nút bấm
 * (tablet treo ngoài xưởng, chạm nhầm là dừng máy thật). Máy sắp xong nằm trên
 * cùng và máy vừa được đọc loa thì nháy sáng, để người nghe ngẩng lên là thấy.
 */

import { memo } from 'react'

import type { TeaMachine } from '@/lib/tea-machines/types'
import { computeElapsed } from '@/lib/tea-machines/dead-reckoning'
import { remainingSeconds } from '@/lib/tea-machines/board-sort'
import { statusLabel } from '@/components/tea-machines/machine-status'
import { m } from '@/lib/paraglide/messages'

function formatOutageTime(timestamp: number) {
    return new Date(timestamp).toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Số phút còn lại hiển thị: làm tròn LÊN để không hứa sớm hơn thực tế. */
export function remainingLabel(machine: TeaMachine, nowMs: number): string {
    const sec = remainingSeconds(machine, nowMs)
    if (!Number.isFinite(sec)) return '—'
    return m.station_machine_remaining_min({ minutes: Math.ceil(sec / 60) })
}

interface RowProps {
    machine: TeaMachine
    nowMs: number
    isHighlighted: boolean
}

function StationMachineRowImpl({ machine, nowMs, isHighlighted }: RowProps) {
    const { displayMin, ratio, finishing } = computeElapsed(machine, nowMs)
    const isRunning = machine.status === 'running'
    const hasTarget = machine.configuredMin > 0

    return (
        <div
            data-testid={`station-machine-${machine.code}`}
            className={`board-grid machine-row ${machine.status} ${
                isHighlighted ? 'arrive' : ''
            }`}
        >
            {/* Mã máy — mỏ neo của cả hàng */}
            <span className="text-2xl font-black tracking-wide text-[var(--ink)]">
                {machine.code}
            </span>

            {/* Trạng thái */}
            <span className="flex">
                <span className={`gate-tag machine-tag-${machine.status}`}>
                    {statusLabel(machine.status)}
                </span>
            </span>

            {/* Tiến độ + phút đã chạy / mức cài */}
            <span className="flex min-w-0 items-center gap-3">
                <span className="machine-track">
                    <span
                        className="machine-fill"
                        style={{ width: `${Math.round(ratio * 100)}%` }}
                    />
                </span>
                <span className="shrink-0 text-base font-bold tabular-nums text-[var(--dim)]">
                    {hasTarget
                        ? `${displayMin}/${machine.configuredMin}`
                        : displayMin}
                </span>
            </span>

            {/* Còn lại — con số người đứng xưởng nhìn nhiều nhất */}
            <span className="text-right text-2xl font-black tabular-nums text-[var(--ink)]">
                {finishing
                    ? m.tea_machines_finishing()
                    : isRunning && hasTarget
                      ? remainingLabel(machine, nowMs)
                      : '—'}
            </span>
        </div>
    )
}

export const StationMachineRow = memo(StationMachineRowImpl)

interface BoardProps {
    machines: TeaMachine[]
    nowMs: number
    highlighted: Set<string>
    /** Chưa có phản hồi đầu tiên từ server. */
    isLoading?: boolean
    /** Gọi API hỏng (mất mạng, SmartLab chết, thiếu quyền → 403). */
    isError?: boolean
    /**
     * Lỗi cấu hình (tài khoản SmartLab bị từ chối, tích hợp tắt) — tự poll
     * chậm lại 5 phút một lần (xem teaMachinesRefetchInterval); nút thử lại
     * cho người đứng trước tablet không muốn chờ sau khi quản trị sửa xong.
     */
    isConfigError?: boolean
    onRetry?: () => void
    staleAt?: number | null
    requestId?: string
}

/** Dòng chữ giữa bảng khi chưa có gì để vẽ. */
function BoardNotice({ text, tone }: { text: string; tone?: 'alert' }) {
    return (
        <div className="flex h-full items-center justify-center px-6">
            <p
                role={tone === 'alert' ? 'alert' : undefined}
                className="empty-blink text-center text-xl tracking-[0.3em]"
                style={{
                    color: tone === 'alert' ? 'var(--alert)' : 'var(--dim)',
                }}
            >
                —&nbsp;&nbsp;{text}&nbsp;&nbsp;—
            </p>
        </div>
    )
}

/** Nút thử lại thủ công — chỉ xuất hiện cho lỗi cấu hình (xem BoardProps). */
function RetryButton({ onRetry }: { onRetry?: () => void }) {
    if (!onRetry) return null
    return (
        <button
            type="button"
            onClick={onRetry}
            className="station-chrome rounded-full border border-current px-5 py-1.5 text-sm font-semibold"
        >
            {m.tea_machines_retry()}
        </button>
    )
}

export function StationMachineBoard({
    machines,
    nowMs,
    highlighted,
    isLoading,
    isError,
    isConfigError,
    onRetry,
    staleAt,
    requestId,
}: BoardProps) {
    // Bảng treo tường không ai trông: hỏng mà vẫn hiện "chưa có máy nào" thì cả
    // buổi không ai biết. Ba trạng thái phải nói ba câu khác nhau.
    if (isError && machines.length === 0) {
        if (isConfigError) {
            return (
                <div className="flex h-full flex-col items-center justify-center gap-4 px-6">
                    <p
                        role="alert"
                        className="empty-blink text-center text-xl tracking-[0.3em]"
                        style={{ color: 'var(--dim)' }}
                    >
                        —&nbsp;&nbsp;{m.tea_machines_config_error()}
                        &nbsp;&nbsp;—
                    </p>
                    <RetryButton onRetry={onRetry} />
                </div>
            )
        }
        return <BoardNotice text={m.station_machines_error()} tone="alert" />
    }
    if (isLoading && machines.length === 0) {
        return <BoardNotice text={m.station_machines_loading()} />
    }
    if (machines.length === 0) {
        return <BoardNotice text={m.station_machines_empty()} />
    }
    return (
        <div className="min-w-[720px]">
            {staleAt !== null &&
                staleAt !== undefined &&
                (isConfigError ? (
                    <div
                        role="alert"
                        className="station-chrome flex items-center justify-center gap-3 border-b border-[var(--line)] px-5 py-2 text-center text-sm font-semibold"
                    >
                        <span>{m.tea_machines_config_error()}</span>
                        <RetryButton onRetry={onRetry} />
                    </div>
                ) : (
                    <div
                        role="alert"
                        className="station-chrome border-b border-[var(--alert)] px-5 py-2 text-center text-sm font-semibold text-[var(--alert)]"
                    >
                        {requestId
                            ? m.tea_machines_stale_warning({
                                  time: formatOutageTime(staleAt),
                                  requestId,
                              })
                            : m.tea_machines_stale_warning_without_request_id({
                                  time: formatOutageTime(staleAt),
                              })}
                    </div>
                ))}
            <div className="station-chrome board-grid legend machine-legend">
                <span>{m.tea_machines_col_code()}</span>
                <span>{m.tea_machines_col_status()}</span>
                <span>{m.tea_machines_col_progress()}</span>
                <span className="text-right">
                    {m.station_machine_col_remaining()}
                </span>
            </div>
            <div className="board-list">
                {machines.map((machine) => (
                    <StationMachineRow
                        key={machine.code}
                        machine={machine}
                        nowMs={nowMs}
                        isHighlighted={highlighted.has(machine.code)}
                    />
                ))}
            </div>
        </div>
    )
}
